/**
 * Pair generation utilities for comparison rounds.
 *
 * Full mode: All N*(N-1)/2 pairs (complete round-robin).
 * Partial mode: Block-Overlapping Structure — deterministic, NON-CIRCULAR.
 *   - effectiveBlockCount = max(2, evaluatorCount)  ← always at least 2 blocks
 *   - blockSize = ceil(N × 2 / (effectiveBlockCount + 1)); shift = floor(blockSize / 2)
 *   - Each block uses degree-K non-circular connections (no wraparound past block end).
 *   - Degree is driven by the accuracy parameter (30-100%).
 *     accuracy=100 → full round-robin per evaluator; accuracy=30 → minimal connections.
 *   - For a single evaluator, both blocks are assigned to them.
 *   - Global chain adds any remaining connectivity edges.
 *   - Falls back to even-split full round-robin when N < 8 or blockSize >= N.
 *   - All pairs are globally unique; first block that generates a pair owns it.
 */

export interface EvaluatorPairAssignment {
  evaluatorIndex: number;
  pairs: Array<[number, number]>; // [employee1Id, employee2Id] (min, max)
}

export interface EvaluatorGroupMapping {
  evaluatorId: number;
  employeeIds: number[];
}

/**
 * Returns full round-robin pairs for all N employees.
 * Suitable when there is only one evaluator or N is very small.
 */
export function generateFullPairs(employeeIds: number[]): Array<[number, number]> {
  const pairs: Array<[number, number]> = [];
  for (let i = 0; i < employeeIds.length; i++) {
    for (let j = i + 1; j < employeeIds.length; j++) {
      pairs.push([employeeIds[i], employeeIds[j]]);
    }
  }
  return pairs;
}

/**
 * Block-Overlapping Structure (deterministic, non-circular).
 *
 * @param employeeIds   List of employee IDs to compare.
 * @param evaluatorCount  Number of evaluators.
 * @param accuracy      0-100. Controls the connection degree within each block.
 *                      100 = full round-robin (every possible pair).
 *                      30  = minimal connections (degree ≈ 1-2 per user).
 *                      Default: 70.
 */
export function generatePartialPairAssignments(
  employeeIds: number[],
  evaluatorCount: number,
  accuracy: number = 70
): EvaluatorPairAssignment[] {
  const n = employeeIds.length;

  if (evaluatorCount === 0 || n < 2) return [];

  // At 100% accuracy use full round-robin distributed evenly across evaluators.
  if (accuracy >= 100) {
    const allPairs = generateFullPairs(employeeIds);
    const buckets: Array<Array<[number, number]>> = Array.from(
      { length: evaluatorCount },
      () => []
    );
    allPairs.forEach((pair, i) => {
      buckets[i % evaluatorCount].push(pair);
    });
    return buckets.map((pairs, i) => ({ evaluatorIndex: i, pairs }));
  }

  // Always use at least 2 blocks so a single evaluator still gets sparse coverage.
  const effectiveBlockCount = Math.max(2, evaluatorCount);
  const blockSize = Math.max(2, Math.ceil((n * 2) / (effectiveBlockCount + 1)));

  // For small N (< 8) the sparse block graph gives too few comparisons per employee
  // for Elo ratings to be meaningful. Also catches blockSize >= N degenerate cases.
  if (n < 8 || blockSize >= n) {
    const allPairs = generateFullPairs(employeeIds);
    const buckets: Array<Array<[number, number]>> = Array.from(
      { length: evaluatorCount },
      () => []
    );
    allPairs.forEach((pair, i) => {
      buckets[i % evaluatorCount].push(pair);
    });
    return buckets.map((pairs, i) => ({ evaluatorIndex: i, pairs }));
  }

  const globalPairSet = new Set<string>();
  const buckets: Array<Array<[number, number]>> = Array.from(
    { length: evaluatorCount },
    () => []
  );

  function addPair(a: number, b: number, evalIdx: number): void {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    const key = `${lo}-${hi}`;
    if (!globalPairSet.has(key)) {
      globalPairSet.add(key);
      buckets[evalIdx].push([lo, hi]);
    }
  }

  const shift = Math.max(1, Math.floor(blockSize / 2));
  // Map accuracy (30-99) → degree (1 .. blockSize-1).
  const maxDegree = blockSize - 1;
  const degree = Math.max(1, Math.round((accuracy / 100) * maxDegree));

  // Generate effectiveBlockCount blocks; map each to the nearest real evaluator.
  for (let b = 0; b < effectiveBlockCount; b++) {
    const evalIdx = Math.min(b, evaluatorCount - 1);
    const start = Math.min(b * shift, Math.max(0, n - blockSize));
    const end = Math.min(start + blockSize, n);
    const blockUsers = employeeIds.slice(start, end);
    const bLen = blockUsers.length;

    for (let i = 0; i < bLen; i++) {
      for (let d = 1; d <= degree && i + d < bLen; d++) {
        addPair(blockUsers[i], blockUsers[i + d], evalIdx);
      }
    }
  }

  // Global chain (user[i] ↔ user[i+1]) — guarantees full connectivity
  for (let i = 0; i < n - 1; i++) {
    const evalIdx = Math.min(
      evaluatorCount - 1,
      Math.floor((i * evaluatorCount) / Math.max(1, n - 1))
    );
    addPair(employeeIds[i], employeeIds[i + 1], evalIdx);
  }

  return buckets.map((pairs, i) => ({ evaluatorIndex: i, pairs }));
}

/**
 * Returns the block structure parameters for a given employee + evaluator count.
 * Used by the UI to size the accuracy slider so each position maps to a unique degree.
 *
 * usesFullFallback = true  → partial mode falls back to full round-robin (N<8 or blockSize≥N)
 * maxDegree               → number of distinct accuracy levels (= blockSize − 1)
 * sliderStep              → recommended slider step (ceil(100/maxDegree)) so each step is unique
 *
 * @param isGrouped  When true, uses per-group sizing (maxDegree = groupSize - 1)
 *                   instead of the block-overlap algorithm. groupSize is the
 *                   largest group size (employees / non-FR evaluators).
 */
export function getBlockInfo(
  employeeCount: number,
  evaluatorCount: number,
  isGrouped: boolean = false
): { blockSize: number; maxDegree: number; usesFullFallback: boolean; sliderStep: number } {
  if (employeeCount < 2 || evaluatorCount === 0) {
    return { blockSize: 0, maxDegree: 0, usesFullFallback: true, sliderStep: 5 };
  }

  // Grouped mode: accuracy maps to degree 1..(groupSize-1) within each group.
  // Use the largest group size (ceil division) for slider bounds.
  if (isGrouped) {
    const groupEvaluators = Math.max(1, evaluatorCount - 1); // exclude FR
    const groupSize = Math.ceil(employeeCount / groupEvaluators);
    if (groupSize < 3) {
      // Groups of 2 or less — only 1 pair possible, slider is meaningless
      return { blockSize: groupSize, maxDegree: 0, usesFullFallback: true, sliderStep: 5 };
    }
    const maxDegree = groupSize - 1;
    const sliderStep = Math.max(1, Math.ceil(100 / maxDegree));
    return { blockSize: groupSize, maxDegree, usesFullFallback: false, sliderStep };
  }

  const effectiveBlockCount = Math.max(2, evaluatorCount);
  const blockSize = Math.max(2, Math.ceil((employeeCount * 2) / (effectiveBlockCount + 1)));
  if (employeeCount < 8 || blockSize >= employeeCount) {
    return { blockSize, maxDegree: 0, usesFullFallback: true, sliderStep: 5 };
  }
  const maxDegree = blockSize - 1;
  const sliderStep = Math.max(1, Math.ceil(100 / maxDegree));
  return { blockSize, maxDegree, usesFullFallback: false, sliderStep };
}

/**
 * Returns the estimated total number of comparisons across all evaluators
 * for the given mode, employee count, and evaluator count.
 *
 * @param accuracy  Only used for partial mode (30-100). Default: 70.
 * @param isGrouped When true, estimates using per-group pair generation (grouped mode).
 */
export function getEstimatedComparisons(
  employeeCount: number,
  mode: 'full' | 'partial',
  evaluatorCount: number,
  accuracy: number = 70,
  isGrouped: boolean = false
): { total: number; perEvaluator: number } {
  if (employeeCount < 2 || evaluatorCount === 0) {
    return { total: 0, perEvaluator: 0 };
  }

  if (isGrouped) {
    // Grouped mode: each non-FR evaluator gets their own group
    const groupEvaluators = Math.max(1, evaluatorCount - 1); // exclude FR
    const baseSize = Math.floor(employeeCount / groupEvaluators);
    const remainder = employeeCount % groupEvaluators;

    // Simulate groups and generate pairs per group
    const dummyMappings: EvaluatorGroupMapping[] = [];
    let offset = 0;
    for (let g = 0; g < groupEvaluators; g++) {
      const gSize = baseSize + (g < remainder ? 1 : 0);
      const start = offset;
      const empIds = Array.from({ length: gSize }, (_, i) => start + i + 1);
      offset += gSize;
      dummyMappings.push({ evaluatorId: g + 1, employeeIds: empIds });
    }

    const groupResults = generateGroupPairs(dummyMappings, mode, accuracy);
    const total = groupResults.reduce((s, g) => s + g.pairs.length, 0);
    const perEvaluator = groupEvaluators > 0 ? Math.round(total / groupEvaluators) : 0;
    return { total, perEvaluator };
  }

  if (mode === 'full') {
    // One shared round: all N*(N-1)/2 pairs are done once, distributed across evaluators
    const total = (employeeCount * (employeeCount - 1)) / 2;
    const perEvaluator = Math.ceil(total / evaluatorCount);
    return { total, perEvaluator };
  }

  // Partial: compute using dummy IDs
  const dummyIds = Array.from({ length: employeeCount }, (_, i) => i + 1);
  const assignments = generatePartialPairAssignments(dummyIds, evaluatorCount, accuracy);
  const totals = assignments.map(a => a.pairs.length);
  const total = totals.reduce((s, c) => s + c, 0);
  const perEvaluator = totals.length > 0
    ? Math.round(total / totals.length)
    : 0;
  return { total, perEvaluator };
}

/**
 * Generates pair assignments for grouped mode.
 *
 * Each evaluator receives pairs for only the employees in their group.
 * Full mode  → all N*(N-1)/2 within-group pairs.
 * Partial mode → degree-based pairs within the group. The degree is derived
 *   from accuracy (0-100) mapped to 1..(N-1). This avoids the N<8 full-fallback
 *   in generatePartialPairAssignments which would ignore the accuracy setting
 *   for the typically small group sizes seen in grouped mode.
 *
 * Returns one entry per group mapping, keyed by evaluatorId.
 */
export function generateGroupPairs(
  groupMappings: EvaluatorGroupMapping[],
  mode: 'full' | 'partial',
  accuracy: number = 70
): Array<{ evaluatorId: number; pairs: Array<[number, number]> }> {
  return groupMappings.map(({ evaluatorId, employeeIds }) => {
    const n = employeeIds.length;
    if (n < 2) {
      return { evaluatorId, pairs: [] };
    }
    if (mode === 'full' || accuracy >= 100) {
      return { evaluatorId, pairs: generateFullPairs(employeeIds) };
    }

    // Partial: degree-based pair generation within the group.
    // maxDegree = N-1 (full round-robin), degree scales with accuracy.
    const maxDegree = n - 1;
    const degree = Math.max(1, Math.round((accuracy / 100) * maxDegree));

    const pairSet = new Set<string>();
    const pairs: Array<[number, number]> = [];
    const addPair = (a: number, b: number) => {
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      const key = `${lo}-${hi}`;
      if (!pairSet.has(key)) {
        pairSet.add(key);
        pairs.push([lo, hi]);
      }
    };

    // Each employee connects to the next `degree` employees (non-circular)
    for (let i = 0; i < n; i++) {
      for (let d = 1; d <= degree && i + d < n; d++) {
        addPair(employeeIds[i], employeeIds[i + d]);
      }
    }

    return { evaluatorId, pairs };
  });
}
